// Unified data layer for Messaging. Supabase when configured, demo store otherwise.

import { supabase, isSupabaseConfigured } from "./supabase";
import {
  demoCreateConversation,
  demoGetConversation,
  demoHasSentParentMessage,
  demoListConversations,
  demoMarkRead,
  demoSendMessage,
} from "./messagingDemo";
import {
  transformedSignedUrl,
  prepareImageForUpload,
} from "./imageOptimization";

export async function listConversations({ profileId, role }) {
  if (!isSupabaseConfigured) return demoListConversations(profileId, { role });
  const { data, error } = await supabase
    .from("conversations")
    .select(
      `id, organization_id, type, subject, classroom_id, family_id,
       created_by, last_message_at, created_at,
       messages(id, body, sender_id, created_at),
       conversation_participants!inner(profile_id, last_read_at)`,
    )
    .order("last_message_at", { ascending: false });
  if (error) throw error;
  // Shape: compute unread + last_message.
  return (data || []).map((c) => {
    const msgs = (c.messages || []).sort((a, b) =>
      b.created_at.localeCompare(a.created_at),
    );
    const last_message = msgs[0] || null;
    const selfPart = (c.conversation_participants || []).find(
      (p) => p.profile_id === profileId,
    );
    const lastRead = selfPart?.last_read_at;
    const unread_count = msgs.filter(
      (m) => m.sender_id !== profileId && (!lastRead || m.created_at > lastRead),
    ).length;
    return { ...c, messages: undefined, last_message, unread_count };
  });
}

export async function getConversation({ conversationId, profileId }) {
  if (!isSupabaseConfigured) return demoGetConversation(conversationId, profileId);
  const { data, error } = await supabase
    .from("conversations")
    .select(
      `id, organization_id, type, subject, classroom_id, family_id,
       created_by, last_message_at, created_at,
       messages(id, body, sender_id, created_at, edited_at,
                message_attachments(id, file_name, mime_type, byte_size, storage_path, uploader_id, created_at)),
       conversation_participants(profile_id, last_read_at)`,
    )
    .eq("id", conversationId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const selfPart = (data.conversation_participants || []).find(
    (p) => p.profile_id === profileId,
  );
  return {
    ...data,
    messages: (data.messages || []).sort((a, b) =>
      a.created_at.localeCompare(b.created_at),
    ),
    last_read_at: selfPart?.last_read_at || null,
  };
}

export async function sendMessage({ conversationId, senderId, body }) {
  if (!isSupabaseConfigured) {
    return demoSendMessage({ conversationId, senderId, body });
  }
  const { data, error } = await supabase
    .from("messages")
    .insert({ conversation_id: conversationId, sender_id: senderId, body })
    .select()
    .single();
  if (error) throw error;
  return data;
}

/** Whether this parent has sent a message, using the live or demo contract. */
export async function hasSentMessage(profileId) {
  if (!profileId) return false;
  if (!isSupabaseConfigured) return demoHasSentParentMessage(profileId);
  const { count, error } = await supabase
    .from("messages")
    .select("id", { count: "exact", head: true })
    .eq("sender_id", profileId);
  if (error) throw error;
  return (count || 0) > 0;
}

export async function markConversationRead({ conversationId, profileId }) {
  if (!isSupabaseConfigured) {
    demoMarkRead({ conversationId, profileId });
    notifyReadChanged();
    return;
  }
  const { error } = await supabase
    .from("conversation_participants")
    .update({ last_read_at: new Date().toISOString() })
    .eq("conversation_id", conversationId)
    .eq("profile_id", profileId);
  if (error) throw error;
  notifyReadChanged();
}

/**
 * Fired after a successful mark-read so cross-component consumers
 * (primarily useUnreadMessages for the nav badge) can refresh instantly
 * instead of waiting for the 60s poll. Separate from realtime inbox
 * pings, which only fire on NEW messages.
 */
function notifyReadChanged() {
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("messaging:read-changed"));
    }
  } catch {
    /* ignore in non-DOM contexts */
  }
}

// ---------------------------------------------------------------------------
// Attachments (Phase 3 Module 13)
// ---------------------------------------------------------------------------

export const ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;  // 25 MB — matches storage bucket limit
export const ATTACHMENT_MAX_PER_MESSAGE = 6;
export const ATTACHMENT_ACCEPT = [
  "image/jpeg", "image/png", "image/gif", "image/webp", "image/heic", "image/heif",
  "application/pdf",
  "video/mp4", "video/quicktime",
  "text/plain",
];

/**
 * Upload `files` to the `message-attachments` bucket and insert matching
 * rows in `message_attachments`. Called AFTER the parent message is
 * created (we need its id for the RLS self-uploader check).
 *
 * Returns the inserted attachment rows in order. Throws on the first
 * failure so the caller can surface a single error — attachment rows
 * are not transactional with the message body, so partial failure
 * leaves an incomplete message. The Thread UI handles this by refusing
 * to send if the upload fails, and retries are safe (dedup via uuid
 * paths).
 */
export async function uploadMessageAttachments({
  conversationId, messageId, organizationId, uploaderId, files,
}) {
  if (!isSupabaseConfigured || !files || files.length === 0) return [];
  if (files.length > ATTACHMENT_MAX_PER_MESSAGE) {
    throw new Error(`You can attach up to ${ATTACHMENT_MAX_PER_MESSAGE} files per message.`);
  }

  const rows = [];
  for (const f of files) {
    if (f.size > ATTACHMENT_MAX_BYTES) {
      throw new Error(`"${f.name}" is larger than 25 MB.`);
    }
    // Best-effort downscale for image attachments; non-images pass through.
    // Saves bandwidth on both upload (sender) and every recipient render.
    const optimised = await prepareImageForUpload(f).catch(() => f);
    const useFile = optimised || f;
    // Uniqueness: uuid per file under <conv>/<msg>/...
    const ext = (useFile.name.split(".").pop() || "bin").toLowerCase().replace(/[^a-z0-9]/g, "") || "bin";
    const key = `${conversationId}/${messageId}/${uniqueSuffix()}.${ext}`;
    const up = await supabase.storage
      .from("message-attachments")
      .upload(key, useFile, {
        contentType: useFile.type || "application/octet-stream",
        upsert: false,
      });
    if (up.error) throw up.error;

    const { data: row, error } = await supabase
      .from("message_attachments")
      .insert({
        message_id:      messageId,
        conversation_id: conversationId,
        organization_id: organizationId,
        uploader_id:     uploaderId,
        storage_path:    key,
        file_name:       f.name.slice(0, 200),
        mime_type:       useFile.type || f.type || null,
        byte_size:       useFile.size,
      })
      .select()
      .single();
    if (error) throw error;
    rows.push(row);
  }
  return rows;
}

/**
 * Resolve a signed download URL for a single attachment path. 1h TTL —
 * regenerated each time the Thread mounts (the Storage API caches
 * server-side so repeat calls are cheap).
 */
export async function getAttachmentSignedUrl(storagePath) {
  if (!isSupabaseConfigured || !storagePath) return null;
  const { data, error } = await supabase.storage
    .from("message-attachments")
    .createSignedUrl(storagePath, 60 * 60);
  if (error) return null;
  return data?.signedUrl || null;
}

export async function getAttachmentSignedUrls(paths) {
  if (!isSupabaseConfigured || !paths || paths.length === 0) return {};
  const { data, error } = await supabase.storage
    .from("message-attachments")
    .createSignedUrls(paths, 60 * 60);
  if (error) return {};
  const out = {};
  for (const r of data || []) {
    if (r.path && r.signedUrl) out[r.path] = r.signedUrl;
  }
  return out;
}

/**
 * Resolve a small, render-only signed URL for an image attachment with
 * a server-side transform applied (default: 800px width, q=75). Falls
 * back to the un-transformed signed URL when transforms are unavailable.
 * Click-through / download paths should still use {@link getAttachmentSignedUrls}.
 */
export async function getAttachmentImageSrc(path, { width = 800, quality = 75 } = {}) {
  if (!isSupabaseConfigured || !path) return null;
  return transformedSignedUrl("message-attachments", path, { width, quality, resize: "cover" });
}

// ---------------------------------------------------------------------------
// Realtime (Phase 3 Module 7) — Supabase channel subscriptions.
// Callers invoke these in useEffect and keep the returned cleanup.
// ---------------------------------------------------------------------------

// Per-subscription unique suffix so two callers (shell hook + Inbox page) or
// a StrictMode double-mount never collide on the same channel topic.
function uniqueSuffix() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch {
    /* ignore */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Subscribe to new messages in a specific conversation.
 * Fires `onInsert(messageRow)` on every INSERT. RLS still applies at the
 * row level so callers never see messages they can't already read.
 */
export function subscribeToConversationMessages(conversationId, onInsert) {
  if (!isSupabaseConfigured || !conversationId) return () => {};
  // Unique topic per subscription. Supabase-js keeps a per-client map keyed
  // by topic string; sharing a topic across callers (or across StrictMode
  // double-mounts) causes `cannot add postgres_changes callbacks ... after
  // subscribe()`. Randomizing the suffix isolates each caller's channel.
  const topic = `conversation:${conversationId}:${uniqueSuffix()}`;
  let channel;
  try {
    channel = supabase
      .channel(topic)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          try {
            onInsert(payload.new);
          } catch {
            /* never let a single event crash the UI */
          }
        },
      )
      .subscribe();
  } catch {
    return () => {};
  }
  return () => {
    try {
      if (channel) supabase.removeChannel(channel);
    } catch {
      /* ignore */
    }
  };
}

/**
 * Subscribe to any new messages anywhere the caller can see. Fires
 * `onChange()` (no payload) — the caller re-fetches the inbox. Used by
 * the shell nav badge + inbox page.
 */
export function subscribeToInboxPings(onChange) {
  if (!isSupabaseConfigured) return () => {};
  // Unique topic per call — both the shell unread hook and the Inbox page
  // subscribe simultaneously, and React StrictMode double-invokes effects
  // in dev/preview. A shared topic returns the same channel instance and
  // throws `cannot add postgres_changes callbacks ... after subscribe()`.
  const topic = `inbox-pings:${uniqueSuffix()}`;
  let channel;
  try {
    channel = supabase
      .channel(topic)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "messages" },
        () => {
          try {
            onChange();
          } catch {
            /* ignore */
          }
        },
      )
      .subscribe();
  } catch {
    return () => {};
  }
  return () => {
    try {
      if (channel) supabase.removeChannel(channel);
    } catch {
      /* ignore */
    }
  };
}

/**
 * Subscribe to typing broadcasts in a conversation + get a function to
 * broadcast your own typing state. Uses Supabase's `broadcast` channel
 * type (not presence) because typing is transient and per-message — we
 * don't need join/leave semantics.
 *
 * Contract:
 *   * `onChange(entries)` fires whenever the set of currently-typing
 *     profiles changes. `entries` is an array of { profileId, name,
 *     expiresAt }. Entries auto-expire 4.5s after the last broadcast
 *     from that profile (the sender broadcasts every ~2s while typing).
 *   * `broadcastTyping()` sends your profile id + display name on the
 *     channel; caller should throttle (we throttle to every 1.8s below
 *     so the viewer's 4.5s window stays warm).
 *
 * Returns `{ unsubscribe, broadcastTyping }`. Demo mode returns no-ops.
 */
export function subscribeToConversationTyping({
  conversationId, profileId, profileName, onChange,
}) {
  if (!isSupabaseConfigured || !conversationId || !profileId) {
    return { unsubscribe: () => {}, broadcastTyping: () => {} };
  }
  // Deterministic topic per conversation — ALL participants must share
  // the same topic so their broadcasts reach each other. This differs
  // from the postgres_changes topics (which use a uniqueSuffix to avoid
  // callback-collision errors under StrictMode double-mount): broadcast
  // doesn't have that failure mode, and a per-client suffix would
  // isolate each tab into its own echo chamber.
  const topic = `typing:${conversationId}`;
  const active = new Map(); // profileId -> { profileId, name, expiresAt }
  let pruneTimer = null;

  function emit() {
    try {
      onChange(Array.from(active.values()));
    } catch {
      /* never let a single event crash the UI */
    }
  }
  function prune() {
    const now = Date.now();
    let changed = false;
    for (const [pid, entry] of active.entries()) {
      if (entry.expiresAt <= now) {
        active.delete(pid);
        changed = true;
      }
    }
    if (changed) emit();
    if (active.size === 0 && pruneTimer) {
      clearInterval(pruneTimer);
      pruneTimer = null;
    }
  }

  let channel;
  try {
    channel = supabase
      .channel(topic, { config: { broadcast: { self: false } } })
      .on("broadcast", { event: "typing" }, ({ payload }) => {
        if (!payload || !payload.profileId) return;
        if (payload.profileId === profileId) return; // never echo self
        active.set(payload.profileId, {
          profileId: payload.profileId,
          name: payload.name || "Someone",
          // 4.5s window — senders re-broadcast at ~1.8s while typing.
          expiresAt: Date.now() + 4500,
        });
        if (!pruneTimer) pruneTimer = setInterval(prune, 1000);
        emit();
      })
      .subscribe();
  } catch {
    return { unsubscribe: () => {}, broadcastTyping: () => {} };
  }

  return {
    unsubscribe: () => {
      if (pruneTimer) clearInterval(pruneTimer);
      try {
        if (channel) supabase.removeChannel(channel);
      } catch {
        /* ignore */
      }
    },
    broadcastTyping: () => {
      if (!channel) return;
      try {
        channel.send({
          type: "broadcast",
          event: "typing",
          payload: { profileId, name: profileName || "" },
        });
      } catch {
        /* broadcast is best-effort */
      }
    },
  };
}

/**
 * One-shot unread count. Used by the shell badge. Demo mode falls back
 * to a local computation.
 */
export async function getUnreadMessageCount({ profileId } = {}) {
  if (!isSupabaseConfigured) {
    if (!profileId) return 0;
    try {
      const convs = await demoListConversations(profileId, { role: null });
      return (convs || []).reduce((s, c) => s + (c.unread_count || 0), 0);
    } catch {
      return 0;
    }
  }
  const { data, error } = await supabase.rpc("rpc_unread_message_count");
  if (error) throw error;
  return typeof data === "number" ? data : Number(data) || 0;
}

/**
 * Create a new conversation (and optionally an initial message).
 *
 * @param {object} payload
 *   type: 'direct' | 'family' | 'classroom' | 'announcement'
 *   subject?: string
 *   classroomId?: uuid
 *   familyId?: uuid
 *   createdBy: uuid
 *   organizationId: uuid
 *   participantIds: uuid[]  (demo mode only; in live mode we rely on RLS + participant inserts via a later trigger/RPC)
 *   firstMessageBody?: string
 */
export async function createConversation(payload) {
  if (!isSupabaseConfigured) return demoCreateConversation(payload);

  const { data: conv, error: convErr } = await supabase
    .from("conversations")
    .insert({
      organization_id: payload.organizationId,
      type: payload.type,
      subject: payload.subject || null,
      classroom_id: payload.classroomId || null,
      family_id: payload.familyId || null,
      created_by: payload.createdBy,
    })
    .select()
    .single();
  if (convErr) throw convErr;

  if (payload.participantIds?.length) {
    const rows = Array.from(new Set(payload.participantIds)).map((pid) => ({
      conversation_id: conv.id,
      profile_id: pid,
    }));
    const { error: partErr } = await supabase
      .from("conversation_participants")
      .insert(rows);
    if (partErr) throw partErr;
  }

  let firstMessage = null;
  if (payload.firstMessageBody) {
    firstMessage = await sendMessage({
      conversationId: conv.id,
      senderId: payload.createdBy,
      body: payload.firstMessageBody,
    });
  }
  // Surface the first message so callers (e.g. ComposeDialog with
  // attachments) can pin uploads to it.
  return { ...conv, first_message: firstMessage };
}


/**
 * Find an existing direct conversation between the caller and a single
 * other profile in the same org, or create one. Used by the "Coach this
 * teacher" shortcut on photo moderation.
 *
 * Strategy:
 *   1. List the caller's direct conversations.
 *   2. For each, check whether otherProfileId is also a participant.
 *   3. On a hit, return it. Otherwise create a fresh direct conversation.
 *
 * We avoid a dedicated RPC — this scales fine for our 1-to-hundreds
 * conversation range and reuses existing RLS.
 */
export async function findOrCreateDirectConversation({
  organizationId, createdBy, otherProfileId, subject = null,
}) {
  if (!otherProfileId || !createdBy || !organizationId) {
    throw new Error("findOrCreateDirectConversation: missing args");
  }
  if (!isSupabaseConfigured) {
    return demoCreateConversation({
      type: "direct",
      subject,
      createdBy,
      organizationId,
      participantIds: [createdBy, otherProfileId],
    });
  }

  // 1) Direct conversations the caller is in, within the org.
  const { data: mine, error: mineErr } = await supabase
    .from("conversations")
    .select("id, conversation_participants!inner(profile_id)")
    .eq("organization_id", organizationId)
    .eq("type", "direct")
    .eq("conversation_participants.profile_id", createdBy);
  if (mineErr) throw mineErr;

  const candidateIds = (mine || []).map((c) => c.id);
  if (candidateIds.length > 0) {
    // 2) Narrow to those where the other participant is also a member.
    const { data: hits } = await supabase
      .from("conversation_participants")
      .select("conversation_id")
      .in("conversation_id", candidateIds)
      .eq("profile_id", otherProfileId);
    const hitId = (hits || [])[0]?.conversation_id;
    if (hitId) return { id: hitId };
  }

  // 3) Create a fresh one.
  return createConversation({
    type: "direct",
    subject,
    createdBy,
    organizationId,
    participantIds: [createdBy, otherProfileId],
  });
}
